import * as THREE from "three";

/**
 * Shared tileable detail texture, generated once: R = fractal value-noise
 * grain (breaks up flat color at boot level), G = warped striation bands
 * (retained for existing consumers), B = broad habitat patches shared
 * with grass coverage and tint, A = angular mineral pigment regions.
 * The original RGB bytes stay stable: rivers and vegetation depend on them.
 * Lattice periods divide the size,
 * so it tiles seamlessly under triplanar repetition.
 */
let detailTex: THREE.DataTexture | null = null;
export const GROUND_DETAIL_SCALE = {fine:.31,habitat:.013,broad:.0045} as const;

export function getDetailTexture(): THREE.DataTexture {
  if (detailTex) return detailTex;
  const N = 256;
  const data = new Uint8Array(N * N * 4);
  const hash = (x: number, y: number, p: number): number => {
    const xi = ((x % p) + p) % p;
    const yi = ((y % p) + p) % p;
    let h = Math.imul(xi * 374761393 + yi * 668265263 + p * 144665, 1274126177);
    h ^= h >>> 16;
    return (h >>> 0) / 4294967295;
  };
  const vnoise = (u: number, v: number, p: number): number => {
    const x = (u * p) % p, y = (v * p) % p;
    const xi = Math.floor(x), yi = Math.floor(y);
    const fx = x - xi, fy = y - yi;
    const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
    const a = hash(xi, yi, p), b = hash(xi + 1, yi, p);
    const c = hash(xi, yi + 1, p), d = hash(xi + 1, yi + 1, p);
    return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
  };
  // Jittered cells produce broad polygonal pigment regions. This is baked
  // once; no cell search, random hash or new texture lookup runs per fragment.
  const mineral = (u:number,v:number):number => {
    const p=4,x=u*p,y=v*p,ix=Math.floor(x),iy=Math.floor(y);
    let nearest=Infinity,value=.5;
    for(let j=-1;j<=1;j++)for(let i=-1;i<=1;i++){
      const a=ix+i,b=iy+j,dx=a+.16+.68*hash(a,b,p)-x,dy=b+.16+.68*hash(a+11,b+7,p)-y;
      const d=dx*dx+dy*dy;
      if(d<nearest){nearest=d;value=hash(a+7,b+19,p);}
    }
    return value;
  };
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      const u = i / N, v = j / N;
      const grain =
        vnoise(u, v, 8) * 0.5 + vnoise(u, v, 16) * 0.3 + vnoise(u, v, 64) * 0.2;
      // Striations: integer band frequency + tileable warp keeps the seam.
      const warp = vnoise(u, v, 8) * 1.2;
      const band = 0.5 + 0.5 * Math.sin((v * 7 + warp) * Math.PI * 2);
      const o = (j * N + i) * 4;
      data[o] = Math.round(grain * 255);
      data[o + 1] = Math.round((band * 0.6 + grain * 0.4) * 255);
      data[o + 2] = Math.round(vnoise(u + 0.31, v + 0.17, 4) * 255);
      data[o + 3] = Math.round(mineral(u,v) * 255);
    }
  }
  detailTex = new THREE.DataTexture(data, N, N);
  detailTex.wrapS = THREE.RepeatWrapping;
  detailTex.wrapT = THREE.RepeatWrapping;
  detailTex.magFilter = THREE.LinearFilter;
  detailTex.minFilter = THREE.LinearMipmapLinearFilter;
  detailTex.generateMipmaps = true;
  detailTex.needsUpdate = true;
  return detailTex;
}

/** The same 5–30 m habitat patches used by the terrain fragment shader.
 * Base-level bilinear sampling is only needed while placing nearby vegetation.
 * Radial projection keeps coverage independent of terrain mesh LOD/normals. */
export function groundPatchAt(x: number, y: number, z: number): number {
  const texture = getDetailTexture();
  const data = texture.image.data as Uint8Array;
  const n = texture.image.width;
  const texel = (u: number, v: number): number => {
    const px = (u - Math.floor(u)) * n - .5, py = (v - Math.floor(v)) * n - .5;
    const ix = Math.floor(px), iy = Math.floor(py), fx = px - ix, fy = py - iy;
    const at = (i: number, j: number) => data[(((j + n) % n) * n + (i + n) % n) * 4 + 2] / 255;
    return (at(ix, iy) * (1 - fx) + at(ix + 1, iy) * fx) * (1 - fy)
      + (at(ix, iy + 1) * (1 - fx) + at(ix + 1, iy + 1) * fx) * fy;
  };
  // Terrain ppos is float32. Match its stable planet-local coordinates.
  x = Math.fround(x); y = Math.fround(y); z = Math.fround(z);
  const sum = Math.abs(x) + Math.abs(y) + Math.abs(z) || 1;
  const s=GROUND_DETAIL_SCALE.habitat;
  return (texel(y * s, z * s) * Math.abs(x)
    + texel(x * s, z * s) * Math.abs(y)
    + texel(x * s, y * s) * Math.abs(z)) / sum;
}

/** Albedo variation only. Geometry and lighting normals stay independent of
 * texture cells and mesh LOD. Mipmaps filter angular pigment edges at distance. */
export const GROUND_DETAIL_GLSL = /* glsl */ `
float groundDetailGain(vec4 fine, vec4 broad, float rock, float sand, float snow, float viewDistance) {
  float grain = mix(.065, .10, rock);
  grain = mix(grain, .018, sand);
  grain = mix(grain, .009, snow);
  float tone = mix(.20, .42, rock);
  tone = mix(tone, .12, sand);
  tone = mix(tone, .045, snow);
  float pigment = mix(broad.r, broad.a, rock * (1.0 - snow) * .85);
  float nearDetail = 1.0 - smoothstep(25.0, 110.0, viewDistance);
  return (1.0 + (fine.r - .5) * grain * nearDetail) * (1.0 + (pigment - .5) * tone);
}
`;
